import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";

const MAX_POLLS = 8;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const { lang } = useT();

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, data: null, error: "missing_session_id" });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ loading: false, data, error: null });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ loading: false, data, error: "timeout" });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: "fetch_failed" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-[#F5F3F0] grain grain-dark flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-xl w-full border border-[#F5F3F0]/20 p-8 md:p-12"
      >
        {state.loading ? (
          <div className="flex flex-col items-center text-center">
            <Loader2 className="animate-spin mb-6 text-[#DDD6CB]" size={32} />
            <div className="chip text-[#A3A3A3]">{lang === "en" ? "Confirming your payment…" : "Betaling verifiëren…"}</div>
          </div>
        ) : state.error === "missing_session_id" ? (
          <>
            <div className="chip text-[#A3A3A3]">— Error</div>
            <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">Missing session</h1>
            <p className="mt-4 text-[#DDD6CB]">{lang === "en" ? "No session id in the URL." : "Geen sessie-id in de URL."}</p>
            <Link to="/" className="btn-light mt-8 inline-flex" data-testid="success-home">
              {lang === "en" ? "Back home" : "Terug naar home"} <ArrowUpRight size={14}/>
            </Link>
          </>
        ) : state.data?.payment_status === "paid" ? (
          <>
            <div className="w-14 h-14 rounded-full border border-[#F5F3F0] flex items-center justify-center mb-6" style={{ background: "var(--joh-accent)", borderColor: "var(--joh-accent)", color: "#0A0A0A" }}>
              <Check size={22} strokeWidth={2} />
            </div>
            <div className="chip text-[#A3A3A3]">— {lang === "en" ? "Payment confirmed" : "Betaling bevestigd"}</div>
            <h1 className="font-display uppercase text-4xl md:text-6xl mt-2 leading-[0.92]" data-testid="success-title">
              {lang === "en" ? "You're in." : "Je zit erin."}
            </h1>
            <p className="mt-6 text-[#DDD6CB] text-base md:text-lg leading-relaxed">
              {state.data.name && (lang === "en" ? `Hey ${state.data.name.split(" ")[0]} — ` : `Hey ${state.data.name.split(" ")[0]} — `)}
              {lang === "en"
                ? `your ${state.data.label} is active. A receipt is on its way to your inbox.`
                : `je ${state.data.label} is actief. De bevestiging staat in je inbox.`}
            </p>

            {/* Order summary */}
            <div className="mt-8 border border-[#F5F3F0]/25 divide-y divide-[#F5F3F0]/10" data-testid="success-summary">
              <div className="flex justify-between px-5 py-3">
                <span className="chip text-[#A3A3A3]">{lang === "en" ? "Purchase" : "Aankoop"}</span>
                <span className="font-display uppercase text-lg text-[#F5F3F0]">{state.data.label}</span>
              </div>
              <div className="flex justify-between px-5 py-3">
                <span className="chip text-[#A3A3A3]">{lang === "en" ? "Amount" : "Bedrag"}</span>
                <span className="font-display text-xl text-[#F5F3F0]">€{(state.data.amount / 100).toLocaleString("nl-NL")}</span>
              </div>
              {state.data.kind === "membership" && (
                <div className="flex justify-between px-5 py-3">
                  <span className="chip text-[#A3A3A3]">{lang === "en" ? "Renews" : "Verlengt"}</span>
                  <span className="chip accent-ink" style={{ color: "var(--joh-accent)" }}>{lang === "en" ? "Monthly · cancel anytime" : "Maandelijks · altijd opzegbaar"}</span>
                </div>
              )}
              {state.data.credits > 0 && (
                <div className="flex justify-between px-5 py-3">
                  <span className="chip text-[#A3A3A3]">{lang === "en" ? "Credits added" : "Credits toegevoegd"}</span>
                  <span className="font-display text-2xl text-[#F5F3F0]" data-testid="success-credits">+{state.data.credits}</span>
                </div>
              )}
              {state.data.class_label && (
                <div className="flex justify-between px-5 py-3">
                  <span className="chip text-[#A3A3A3]">{lang === "en" ? "Your class" : "Jouw les"}</span>
                  <span className="chip text-[#F5F3F0]" data-testid="success-class-label">{state.data.class_label}</span>
                </div>
              )}
              {state.data.next_class && (
                <div className="flex justify-between px-5 py-3">
                  <span className="chip text-[#A3A3A3]">{lang === "en" ? "First class" : "Eerste les"}</span>
                  <span className="chip text-[#F5F3F0]">{new Date(state.data.next_class.date).toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL", { weekday: "short", day: "numeric", month: "short" })} · {state.data.next_class.start}</span>
                </div>
              )}
              {state.data.email && (
                <div className="flex justify-between px-5 py-3">
                  <span className="chip text-[#A3A3A3]">{lang === "en" ? "Receipt sent to" : "Bevestiging naar"}</span>
                  <span className="chip text-[#F5F3F0] truncate max-w-[240px]" data-testid="success-email">{state.data.email}</span>
                </div>
              )}
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Link
                to={`/mij${state.data.email ? `?email=${encodeURIComponent(state.data.email)}` : ""}`}
                className="btn-light w-full justify-center"
                data-testid="success-open-portal"
              >
                {lang === "en" ? "Open my portal" : "Open mijn portal"} <ArrowUpRight size={14}/>
              </Link>
              <Link to="/#rsvp" className="btn-ghost text-[#F5F3F0] border-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A] w-full justify-center" data-testid="success-see-schedule">
                {lang === "en" ? "Book my first class" : "Boek mijn eerste les"} <ArrowUpRight size={14}/>
              </Link>
            </div>
            <p className="chip text-[#A3A3A3] mt-6 text-center">
              {lang === "en"
                ? "Track your credits, upcoming classes and waitlist in your portal."
                : "Volg je credits, komende lessen en reservelijst in je portal."}
            </p>
          </>
        ) : (
          <>
            <div className="chip text-[#A3A3A3]">— {lang === "en" ? "Still processing" : "Nog aan het verwerken"}</div>
            <h1 className="font-display uppercase text-4xl md:text-5xl mt-2">
              {lang === "en" ? "Almost there." : "Bijna klaar."}
            </h1>
            <p className="mt-4 text-[#DDD6CB]">
              {lang === "en"
                ? "Your payment is still being confirmed. You'll receive an email as soon as it's completed."
                : "Je betaling wordt nog bevestigd. Zodra het klaar is krijg je een e-mail."}
            </p>
            <Link to="/" className="btn-light mt-8 inline-flex" data-testid="success-home">
              {lang === "en" ? "Back home" : "Terug naar home"} <ArrowUpRight size={14}/>
            </Link>
          </>
        )}
      </motion.div>
    </main>
  );
}
